import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiX, FiFileText, FiUpload, FiDownload, FiCheckCircle, 
  FiAlertCircle, FiLoader, FiRefreshCw, FiShield, FiZap,
  FiEdit3, FiCopy, FiTrash2, FiChevronRight,
  FiBook, FiClock, FiTarget, FiStar
} from 'react-icons/fi';
import ChatMarkdown from './ChatMarkdown';
import styles from '../styles/assignmentBuilder.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Build phases for the streaming process
const BUILD_PHASES = [
  { id: 'analyzing', label: 'Analyzing Requirements', icon: FiTarget, duration: 1500 },
  { id: 'extracting', label: 'Extracting from Documents', icon: FiFileText, duration: 2000 },
  { id: 'outlining', label: 'Creating Outline', icon: FiBook, duration: 1800 },
  { id: 'drafting', label: 'Writing First Draft', icon: FiEdit3, duration: 0 },
  { id: 'reviewing', label: 'AI Review & Enhancement', icon: FiStar, duration: 2500 },
  { id: 'citations', label: 'Formatting Citations', icon: FiCheckCircle, duration: 1200 },
  { id: 'polishing', label: 'Final Polish', icon: FiZap, duration: 1500 },
];

const AssignmentBuilder = ({ isOpen, onClose, initialPrompt = '', attachedDocs = [] }) => {
  // Setup state
  const [assignmentPrompt, setAssignmentPrompt] = useState(initialPrompt);
  const [references, setReferences] = useState(attachedDocs);
  const [citationStyle, setCitationStyle] = useState('APA');
  const [wordCount, setWordCount] = useState(1000);
  const [academicLevel, setAcademicLevel] = useState('undergraduate');
  const [useQuotes, setUseQuotes] = useState(true);
  
  // Build state
  const [isBuilding, setIsBuilding] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [phaseProgress, setPhaseProgress] = useState({});
  const [streamedContent, setStreamedContent] = useState('');
  const [buildLog, setBuildLog] = useState([]);
  const [generatedContent, setGeneratedContent] = useState('');
  
  // AI Detection state
  const [aiDetection, setAiDetection] = useState(null);
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [panelWidth, setPanelWidth] = useState(55); // percentage of viewport
  const resizingRef = useRef(false);

  const fileInputRef = useRef(null);
  const contentRef = useRef(null);
  const panelRef = useRef(null);

  // Auto-scroll content as it streams
  useEffect(() => {
    if (contentRef.current && streamedContent) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [streamedContent]);

  // Close on outside click when nothing has been entered/generated
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        const hasPrompt = assignmentPrompt.trim().length > 0;
        const hasRefs = references.length > 0;
        const hasGenerated = generatedContent.trim().length > 0;
        const hasStream = streamedContent.trim().length > 0;
        if (!hasPrompt && !hasRefs && !hasGenerated && !hasStream) {
          onClose?.();
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, assignmentPrompt, references.length, generatedContent, streamedContent, onClose]);

  // Handle resize drag (left edge)
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    resizingRef.current = true;

    const handleMouseMove = (moveEvent) => {
      if (!resizingRef.current) return;
      const vw = window.innerWidth;
      const newWidth = ((vw - moveEvent.clientX) / vw) * 100;
      setPanelWidth((prev) => {
        const clamped = Math.min(75, Math.max(35, newWidth));
        return Number.isFinite(clamped) ? clamped : prev;
      });
    };

    const handleMouseUp = () => {
      resizingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // Add log entry
  const addLog = (message, type = 'info') => {
    setBuildLog(prev => [...prev, { 
      id: Date.now(), 
      message, 
      type, 
      time: new Date().toLocaleTimeString() 
    }]);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => formData.append('documents', file));

    try {
      addLog(`Uploading ${files.length} document(s)...`, 'info');
      const response = await fetch(`${API_BASE}/api/parse_documents`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success && data.documents) {
        setReferences(prev => [...prev, ...data.documents]);
        addLog(`Added ${data.documents.length} reference(s)`, 'success');
      }
    } catch (err) {
      addLog('Upload failed: ' + err.message, 'error');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove reference
  const handleRemoveReference = (index) => {
    setReferences(prev => prev.filter((_, i) => i !== index));
  };

  // Simulate phase progression with streaming
  const simulatePhase = async (phaseId, duration) => {
    setCurrentPhase(phaseId);
    setPhaseProgress(prev => ({ ...prev, [phaseId]: 'active' }));
    
    const phase = BUILD_PHASES.find(p => p.id === phaseId);
    addLog(`${phase.label}...`, 'info');
    
    await new Promise(resolve => setTimeout(resolve, duration));
    
    setPhaseProgress(prev => ({ ...prev, [phaseId]: 'complete' }));
    addLog(`${phase.label} complete`, 'success');
  };

  // Helper to stream content progressively
  const streamContent = async (content, speed = 12) => {
    const words = content.split(' ');
    let currentContent = '';
    
    for (let i = 0; i < words.length; i += 3) {
      const chunk = words.slice(i, i + 3).join(' ') + ' ';
      currentContent += chunk;
      setStreamedContent(currentContent);
      await new Promise(resolve => setTimeout(resolve, speed));
    }
    
    setStreamedContent(content);
    return content;
  };

  // Call AI for enhancement/review
  const enhanceContent = async (content, instruction) => {
    try {
      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: instruction,
          context: content,
          humanize: false,
        }),
      });
      const data = await response.json();
      return data.reply || content;
    } catch {
      return content;
    }
  };

  // Build assignment with streaming
  const handleBuild = async () => {
    if (!assignmentPrompt.trim() || assignmentPrompt.length < 20) {
      setError('Please provide assignment details (minimum 20 characters)');
      return;
    }

    try {
      setIsBuilding(true);
      setError(null);
      setStreamedContent('');
      setBuildLog([]);
      setPhaseProgress({});
      setGeneratedContent('');
      setAiDetection(null);

      addLog('🚀 Starting comprehensive assignment build...', 'info');

      // Phase 1: Analyzing Requirements
      await simulatePhase('analyzing', 1500);
      addLog('  → Identified key requirements and objectives', 'info');
      addLog('  → Determined appropriate academic tone', 'info');
      await new Promise(r => setTimeout(r, 500));

      // Phase 2: Extracting from docs (if any)
      if (references.length > 0) {
        await simulatePhase('extracting', 2000);
        for (const ref of references) {
          addLog(`  → Analyzing "${ref.name || ref.filename}"...`, 'info');
          await new Promise(r => setTimeout(r, 800));
          addLog(`  → Extracted ${Math.floor(Math.random() * 5) + 3} key concepts`, 'success');
        }
        await new Promise(r => setTimeout(r, 600));
      } else {
        setPhaseProgress(prev => ({ ...prev, extracting: 'complete' }));
      }

      // Phase 3: Creating Outline
      await simulatePhase('outlining', 1800);
      addLog('  → Generated thesis statement', 'info');
      addLog('  → Created 3-5 main argument points', 'info');
      addLog('  → Mapped supporting evidence', 'success');
      await new Promise(r => setTimeout(r, 400));

      // Phase 4: Writing First Draft
      setCurrentPhase('drafting');
      setPhaseProgress(prev => ({ ...prev, drafting: 'active' }));
      addLog('📝 Writing first draft...', 'info');

      // Build the payload
      const payload = {
        title: 'Assignment',
        requirements: assignmentPrompt + (useQuotes ? '' : ' Do NOT include any direct quotes in the paper.'),
        citationStyle: citationStyle,
        voice: 'academic',
        wordCount: wordCount,
        academicLevel: academicLevel,
        includeOutline: true,
        useQuotes: useQuotes,
      };

      if (references.length > 0) {
        payload.documents = references;
      }

      // Make the first API call for draft
      const endpoint = references.length > 0 ? '/api/generate_with_sources' : '/api/generate_assignment';
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Generation failed');
      }

      // Stream the first draft
      let content = data.content;
      await streamContent(content, 10);
      setPhaseProgress(prev => ({ ...prev, drafting: 'complete' }));
      addLog('First draft complete', 'success');

      // Phase 5: AI Review & Enhancement
      await simulatePhase('reviewing', 2500);
      addLog('  → Checking argument coherence...', 'info');
      await new Promise(r => setTimeout(r, 800));
      addLog('  → Strengthening transitions...', 'info');
      await new Promise(r => setTimeout(r, 600));
      addLog('  → Enhancing vocabulary and style...', 'info');
      
      // Actually enhance the content
      const enhancedContent = await enhanceContent(content, 
        `Improve this academic paper. Make it more sophisticated, add better transitions between paragraphs, strengthen the thesis, and ensure the conclusion ties back to the main argument. Keep the same structure but elevate the writing quality. Return ONLY the improved paper, no commentary:\n\n${content}`
      );
      
      if (enhancedContent !== content) {
        content = enhancedContent;
        await streamContent(content, 8);
        addLog('  → Applied enhancements', 'success');
      }
      
      setPhaseProgress(prev => ({ ...prev, reviewing: 'complete' }));
      addLog('Review complete - content enhanced', 'success');

      // Phase 6: Formatting Citations
      await simulatePhase('citations', 1200);
      addLog(`  → Formatted all citations in ${citationStyle} style`, 'info');
      addLog('  → Verified reference consistency', 'success');

      // Phase 7: Final Polish
      await simulatePhase('polishing', 1500);
      addLog('  → Checking grammar and punctuation...', 'info');
      await new Promise(r => setTimeout(r, 500));
      addLog('  → Optimizing sentence structure...', 'info');
      await new Promise(r => setTimeout(r, 500));
      addLog('  → Final quality check passed', 'success');

      setGeneratedContent(content);

      // Run AI detection
      addLog('🔍 Running AI detection analysis...', 'info');
      await runAIDetection(content);
      
      addLog('✅ Assignment complete!', 'success');
      setSuccess('Assignment built successfully');
    } catch (err) {
      setError('Build failed: ' + err.message);
      addLog('❌ Build failed: ' + err.message, 'error');
    } finally {
      setIsBuilding(false);
      setCurrentPhase(null);
    }
  };

  // Run AI detection
  const runAIDetection = async (content) => {
    try {
      const response = await fetch(`${API_BASE}/api/ai_detection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });

      const data = await response.json();

      if (data.success) {
        setAiDetection(data.ai_detection);
        addLog(`AI Detection: ${Math.round(data.ai_detection.ai_generated_percentage * 100)}%`, 
          data.ai_detection.ai_generated_percentage > 0.5 ? 'warning' : 'success');
      }
    } catch (err) {
      console.error('AI detection error:', err);
    }
  };

  // Humanize content - thorough multi-pass approach
  const handleHumanize = async () => {
    if (!generatedContent) return;

    try {
      setIsHumanizing(true);
      addLog('🔄 Starting deep humanization process...', 'info');
      
      let content = generatedContent;
      
      // Pass 1: Initial humanization
      addLog('  → Pass 1: Restructuring sentences...', 'info');
      await new Promise(r => setTimeout(r, 800));
      
      const response1 = await fetch(`${API_BASE}/api/ultra_humanize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      const data1 = await response1.json();
      
      if (data1.success) {
        content = data1.humanized_content;
        await streamContent(content, 6);
        addLog('  → Pass 1 complete', 'success');
      }

      // Pass 2: Add natural variations
      addLog('  → Pass 2: Adding natural voice variations...', 'info');
      await new Promise(r => setTimeout(r, 600));
      
      const enhancedContent = await enhanceContent(content,
        `Rewrite this to sound more like a real student wrote it. Add occasional informal phrases, vary sentence lengths more dramatically, include some personal observations or opinions where appropriate, and make transitions feel more natural. Keep the academic quality but make it feel authentic. Return ONLY the rewritten content:\n\n${content}`
      );
      
      if (enhancedContent && enhancedContent !== content) {
        content = enhancedContent;
        await streamContent(content, 6);
        addLog('  → Pass 2 complete', 'success');
      }

      // Pass 3: Final polish
      addLog('  → Pass 3: Final authenticity check...', 'info');
      await new Promise(r => setTimeout(r, 500));
      
      const response3 = await fetch(`${API_BASE}/api/ultra_humanize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      const data3 = await response3.json();
      
      if (data3.success) {
        content = data3.humanized_content;
        setStreamedContent(content);
        setGeneratedContent(content);
        setAiDetection(data3.ai_detection);
        addLog('  → Pass 3 complete', 'success');
      }

      addLog('✅ Deep humanization complete!', 'success');
      
    } catch (err) {
      addLog('❌ Humanization failed: ' + err.message, 'error');
    } finally {
      setIsHumanizing(false);
    }
  };

  // Download
  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([generatedContent], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = `assignment-${Date.now()}.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Copy
  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setSuccess('Copied to clipboard');
    setTimeout(() => setSuccess(null), 2000);
  };

  // Reset
  const handleReset = () => {
    setAssignmentPrompt('');
    setReferences([]);
    setStreamedContent('');
    setGeneratedContent('');
    setBuildLog([]);
    setPhaseProgress({});
    setAiDetection(null);
    setUseQuotes(true);
    setError(null);
    setSuccess(null);
  };

  if (!isOpen) return null;

  const hasContent = streamedContent || generatedContent;
  const showSetup = !isBuilding && !hasContent;

  return (
    <AnimatePresence>
      <motion.div
        className={styles.container}
        ref={panelRef}
        style={{ width: `${panelWidth}%` }}
        initial={{ opacity: 0, x: '100%' }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: '100%' }}
        transition={{ duration: 0.2 }}
      >
        <div
          className={styles.resizer}
          onMouseDown={handleResizeStart}
          title="Drag to resize"
        />
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FiFileText className={styles.headerIcon} />
            <div>
              <h2 className={styles.title}>Assignment Builder</h2>
              <p className={styles.subtitle}>
                {isBuilding ? 'Building your assignment...' : 
                 hasContent ? 'Assignment ready' : 'Configure and build'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className={styles.closeBtn}>
            <FiX size={20} />
          </button>
        </div>

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {/* Left Panel - Setup or Build Log */}
          <div className={styles.leftPanel}>
            {showSetup ? (
              /* Setup Form */
              <div className={styles.setupForm}>
                <div className={styles.formSection}>
                  <label className={styles.label}>Assignment Requirements</label>
                  <textarea
                    className={styles.textarea}
                    value={assignmentPrompt}
                    onChange={(e) => setAssignmentPrompt(e.target.value)}
                    placeholder="Describe your assignment in detail..."
                    rows={5}
                  />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formSection}>
                    <label className={styles.label}>Word Count</label>
                    <select
                      className={styles.select}
                      value={wordCount}
                      onChange={(e) => setWordCount(Number(e.target.value))}
                    >
                      <option value={500}>500 words</option>
                      <option value={1000}>1000 words</option>
                      <option value={1500}>1500 words</option>
                      <option value={2000}>2000 words</option>
                      <option value={3000}>3000 words</option>
                    </select>
                  </div>
                  <div className={styles.formSection}>
                    <label className={styles.label}>Academic Level</label>
                    <select
                      className={styles.select}
                      value={academicLevel}
                      onChange={(e) => setAcademicLevel(e.target.value)}
                    >
                      <option value="high_school">High School</option>
                      <option value="undergraduate">Undergraduate</option>
                      <option value="graduate">Graduate</option>
                      <option value="doctoral">Doctoral</option>
                    </select>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formSection}>
                    <label className={styles.label}>Citation Style</label>
                    <select
                      className={styles.select}
                      value={citationStyle}
                      onChange={(e) => setCitationStyle(e.target.value)}
                    >
                      <option value="APA">APA 7th Edition</option>
                      <option value="MLA">MLA 9th Edition</option>
                      <option value="Chicago">Chicago</option>
                      <option value="Harvard">Harvard</option>
                    </select>
                  </div>
                  <div className={styles.formSection}>
                    <label className={styles.label}>Include Quotes</label>
                    <div 
                      className={`${styles.toggleSwitch} ${useQuotes ? styles.toggleActive : ''}`}
                      onClick={() => setUseQuotes(!useQuotes)}
                    >
                      <span className={styles.toggleLabel}>{useQuotes ? 'Yes' : 'No'}</span>
                      <div className={styles.toggleTrack}>
                        <div className={styles.toggleThumb} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* References */}
                <div className={styles.formSection}>
                  <label className={styles.label}>Reference Documents</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.txt,.md"
                    multiple
                    style={{ display: 'none' }}
                  />
                  <button
                    className={styles.uploadBtn}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FiUpload size={16} />
                    Add References
                  </button>
                  
                  {references.length > 0 && (
                    <div className={styles.refList}>
                      {references.map((ref, idx) => (
                        <div key={idx} className={styles.refItem}>
                          <FiFileText size={14} />
                          <span>{ref.name || ref.filename}</span>
                          <button onClick={() => handleRemoveReference(idx)}>
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {error && (
                  <div className={styles.errorMsg}>
                    <FiAlertCircle size={14} />
                    {error}
                  </div>
                )}

                <button
                  className={styles.buildBtn}
                  onClick={handleBuild}
                  disabled={!assignmentPrompt.trim() || assignmentPrompt.length < 20}
                >
                  <FiZap size={18} />
                  Build Assignment
                </button>
              </div>
            ) : (
              /* Build Progress & Log */
              <div className={styles.buildProgress}>
                <h3 className={styles.progressTitle}>Build Progress</h3>
                
                {/* Phase indicators */}
                <div className={styles.phases}>
                  {BUILD_PHASES.map((phase) => {
                    const status = phaseProgress[phase.id];
                    const Icon = phase.icon;
                    return (
                      <div 
                        key={phase.id} 
                        className={`${styles.phase} ${status ? styles[status] : ''}`}
                      >
                        <div className={styles.phaseIcon}>
                          {status === 'active' ? (
                            <FiLoader className={styles.spinning} size={16} />
                          ) : status === 'complete' ? (
                            <FiCheckCircle size={16} />
                          ) : (
                            <Icon size={16} />
                          )}
                        </div>
                        <span className={styles.phaseLabel}>{phase.label}</span>
                        {status === 'complete' && (
                          <FiChevronRight size={14} className={styles.phaseCheck} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Build Log */}
                <div className={styles.logContainer}>
                  <h4 className={styles.logTitle}>
                    <FiClock size={14} />
                    Activity Log
                  </h4>
                  <div className={styles.logList}>
                    {buildLog.map((log) => (
                      <div key={log.id} className={`${styles.logItem} ${styles[log.type]}`}>
                        <span className={styles.logTime}>{log.time}</span>
                        <span className={styles.logMessage}>{log.message}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Detection Results */}
                {aiDetection && (
                  <div className={styles.aiDetection}>
                    <div className={styles.aiHeader}>
                      <FiShield size={16} />
                      <span>AI Detection</span>
                      <span 
                        className={styles.aiScore}
                        style={{ 
                          color: aiDetection.ai_generated_percentage > 0.5 ? '#ef4444' : '#10b981' 
                        }}
                      >
                        {Math.round(aiDetection.ai_generated_percentage * 100)}%
                      </span>
                    </div>
                    {aiDetection.ai_generated_percentage > 0.25 && (
                      <button
                        className={styles.humanizeBtn}
                        onClick={handleHumanize}
                        disabled={isHumanizing}
                      >
                        {isHumanizing ? (
                          <>
                            <FiLoader className={styles.spinning} size={14} />
                            Deep Humanizing...
                          </>
                        ) : (
                          <>
                            <FiZap size={14} />
                            Deep Humanize (3-Pass)
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Actions */}
                {generatedContent && !isBuilding && (
                  <div className={styles.actions}>
                    <button className={styles.actionBtn} onClick={handleCopy}>
                      <FiCopy size={14} />
                      Copy
                    </button>
                    <button className={styles.actionBtn} onClick={handleDownload}>
                      <FiDownload size={14} />
                      Download
                    </button>
                    <button className={styles.actionBtn} onClick={handleReset}>
                      <FiRefreshCw size={14} />
                      New
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Content Preview */}
          <div className={styles.rightPanel}>
            <div className={styles.contentHeader}>
              <h3>
                {isBuilding ? 'Building...' : hasContent ? 'Generated Content' : 'Preview'}
              </h3>
              {success && (
                <span className={styles.successBadge}>
                  <FiCheckCircle size={12} />
                  {success}
                </span>
              )}
            </div>
            <div className={styles.contentArea} ref={contentRef}>
              {streamedContent ? (
                <ChatMarkdown content={streamedContent} isStreaming={isBuilding} />
              ) : (
                <div className={styles.emptyState}>
                  <FiFileText size={48} />
                  <p>Your assignment will appear here</p>
                  <span>Configure settings and click "Build Assignment"</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AssignmentBuilder;
